import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const LAST_UPDATED = 'April 2026';
const CONTACT_EMAIL = 'gravitycontextdev@gmail.com';

export default function PrivacyPolicyPage() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | AzureCalc.uk</title>
        <meta
          name="description"
          content="Privacy policy for AzureCalc.uk, covering saved estimates, feedback submissions, browser storage, and Google AdSense."
        />
        <link rel="canonical" href="https://www.azure-calc.co.uk/privacy" />
      </Helmet>
      <div 
        className="min-h-screen"
        style={{ backgroundColor: 'var(--bg-base)' }}
      >
        <div className="max-w-[680px] mx-auto px-4 py-8">

        <Link 
          to="/" 
          className="inline-flex items-center gap-1 text-[12px] mb-6 transition-colors"
          style={{ color: 'var(--text-tertiary)' }}
          onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
          onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-tertiary)'}
        >
          <span className="material-symbols-outlined text-[16px]">arrow_back</span>
          Back to calculators
        </Link>

        <h1 className="text-[26px] font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Privacy Policy</h1>
        <p className="text-[12px] mb-8" style={{ color: 'var(--text-tertiary)' }}>Last updated: {LAST_UPDATED}</p>

        <div className="flex flex-col gap-8 text-[14px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>

          <section>
            <h2 className="text-[16px] font-bold mb-3" style={{ color: 'var(--text-primary)' }}>1. Who we are</h2>
            <p className="mb-2">
              AzureCalc.uk is an independent Azure cost calculator operated by a sole developer.
              Contact:{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#0078d4] hover:underline">{CONTACT_EMAIL}</a>.
            </p>
            <p>
              AzureCalc.uk is not affiliated with, endorsed by, or sponsored by Microsoft Corporation.
            </p>
          </section>

          <section>
            <h2 className="text-[16px] font-bold mb-3" style={{ color: 'var(--text-primary)' }}>2. What data we collect</h2>

            <div className="flex flex-col gap-4">
              <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
                <p className="text-[13px] font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Calculator estimates (anonymous)</p>
                <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                  When you use the "Save &amp; Share" button, your calculator inputs and results are stored.
                  This data contains no personal information — only technical configuration values like storage
                  amounts and pricing tiers.
                </p>
                <p className="text-[12px] mt-2" style={{ color: 'var(--text-tertiary)' }}>Retention: 90 days, then automatically deleted.</p>
              </div>

              <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
                <p className="text-[13px] font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Feedback submissions</p>
                <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                  When you submit feedback via the ❓ button, we collect: issue type, description, and
                  optionally your email address. Your email is only used to follow up on your specific
                  report and is never shared with third parties.
                </p>
                <p className="text-[12px] mt-2" style={{ color: 'var(--text-tertiary)' }}>Retention: 90 days, then automatically deleted.</p>
              </div>

              <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
                <p className="text-[13px] font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Browser localStorage</p>
                <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                  Your saved estimates list is stored in your browser's localStorage. This data never
                  leaves your device and we have no access to it. You can clear it at any time by
                  clearing your browser data.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-[16px] font-bold mb-3" style={{ color: 'var(--text-primary)' }}>3. What we do NOT collect</h2>
            <ul className="list-disc pl-5 flex flex-col gap-1 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
              <li>No account registration or login</li>
              <li>No tracking cookies set by AzureCalc.uk</li>
              <li>No IP address logging in our systems</li>
              <li>No analytics beyond Google AdSense (see Section 5)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-[16px] font-bold mb-3" style={{ color: 'var(--text-primary)' }}>4. Legal basis (UK GDPR Article 6)</h2>
            <div className="flex flex-col gap-3 text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
              <p>
                <strong style={{ color: 'var(--text-secondary)' }}>Feedback email:</strong> Legitimate interest (Article 6(1)(f)) —
                to respond to your specific feedback report.
              </p>
              <p>
                <strong style={{ color: 'var(--text-secondary)' }}>Anonymous estimates:</strong> Not personal data —
                UK GDPR does not apply to anonymous technical configuration data.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-[16px] font-bold mb-3" style={{ color: 'var(--text-primary)' }}>5. Google AdSense</h2>
            <p className="text-[13px] mb-2" style={{ color: 'var(--text-tertiary)' }}>
              This site uses Google AdSense to display advertisements. Google may use cookies to show
              relevant ads based on your browsing history.
            </p>
            <div className="flex flex-col gap-1 text-[13px]">
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0078d4] hover:underline inline-flex items-center gap-1"
              >
                <span className="material-symbols-outlined text-[14px]">open_in_new</span>
                Google Privacy Policy
              </a>
              <a
                href="https://adssettings.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0078d4] hover:underline inline-flex items-center gap-1"
              >
                <span className="material-symbols-outlined text-[14px]">open_in_new</span>
                Opt out of personalised ads
              </a>
            </div>
          </section>

          <section>
            <h2 className="text-[16px] font-bold mb-3" style={{ color: 'var(--text-primary)' }}>6. Your rights under UK GDPR</h2>
            <p className="text-[13px] mb-2" style={{ color: 'var(--text-tertiary)' }}>You have the right to:</p>
            <ul className="list-disc pl-5 flex flex-col gap-1 text-[13px] mb-3" style={{ color: 'var(--text-tertiary)' }}>
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to processing</li>
              <li>Lodge a complaint with the Information Commissioner's Office (ICO)</li>
            </ul>
            <p className="text-[13px] mb-2" style={{ color: 'var(--text-tertiary)' }}>
              To exercise these rights, email{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#0078d4] hover:underline">{CONTACT_EMAIL}</a>.
              I will respond within 30 days.
            </p>
            <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
              To make a complaint to the ICO:{' '}
              <a
                href="https://ico.org.uk/make-a-complaint/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0078d4] hover:underline"
              >
                ico.org.uk/make-a-complaint
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-[16px] font-bold mb-3" style={{ color: 'var(--text-primary)' }}>7. Data storage and security</h2>
            <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
              Anonymous estimate data and feedback are stored in Cloudflare D1 (SQLite database) hosted
              in Cloudflare's EU data centres. Cloudflare is certified under the UK–US Data Bridge and
              EU–US Data Privacy Framework. Personal data (feedback emails) is deleted automatically
              after 90 days.
            </p>
          </section>

          <section>
            <h2 className="text-[16px] font-bold mb-3" style={{ color: 'var(--text-primary)' }}>8. Changes to this policy</h2>
            <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
              I may update this policy. The date at the top of the page will reflect the most recent
              update.
            </p>
          </section>

          </div>
        </div>
      </div>
    </>
  );
}
